import styles from "./ModalWithPicture.module.scss";
import iconStyles from "../Icon.module.scss";
import buttonStyles from "../Button.module.scss";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

export type ModalWithPictureDialogProps = {
  title: string;
  description?: string;
  excerpt?: string;
  image: string;
};

export interface ModalWithPictureHandle {
  onOpen: (dialogProps: ModalWithPictureDialogProps) => void;
}

const ModalWithPicture = forwardRef<ModalWithPictureHandle>((_, ref) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dialogData, setDialogData] = useState<ModalWithPictureDialogProps>({
    title: "",
    excerpt: "",
    description: "",
    image: "",
  });

  useEffect(() => {
    document.body.classList.toggle("stop", isOpen);

    return () => {
      document.body.classList.remove("stop");
    };
  }, [isOpen]);

  const onOpen = (dialogProps: ModalWithPictureDialogProps) => {
    setIsOpen(true);

    setDialogData({
      title: dialogProps.title,
      excerpt: dialogProps.excerpt,
      description: dialogProps.description,
      image: dialogProps.image,
    });
  };

  const onClose = () => {
    setIsOpen(false);
  };

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    onOpen,
  }));

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  return (
    <div
      ref={modalRef}
      className={`${styles.modal} ${isOpen ? styles.active : ""}`}
      style={{
        display: isOpen ? "block" : "none",
      }}
      onClick={handleBackdropClick}
    >
      <div
        ref={dialogRef}
        data-lenis-prevent
        className={`${styles.dialog} ${isOpen ? styles.active : ""}`}
        style={{
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div className={styles.gallery}>
          <img className={styles.img} src={dialogData.image} />
        </div>
        <div className={styles.text} onWheel={(e) => e.stopPropagation()}>
          <div className={styles.desc}>
            <div className={styles.title}>
              <h3>{dialogData.title}</h3>
            </div>
          </div>
          <div className={styles.desc}>
            {dialogData.description && (
              <p
                dangerouslySetInnerHTML={{ __html: dialogData.description }}
              ></p>
            )}
          </div>
          <div className={`${styles.desc} ${styles.info}`}>
            {dialogData.excerpt && (
              <p dangerouslySetInnerHTML={{ __html: dialogData.excerpt }}></p>
            )}
          </div>
        </div>
        <div
          className={`${buttonStyles.btn} ${styles.active} ${styles["close-btn"]}`}
          onClick={onClose}
        >
          <span
            className={`${iconStyles.icon} ${styles.icon} ${iconStyles.close}`}
          ></span>
        </div>
      </div>
    </div>
  );
});

export default ModalWithPicture;
